/**
 * How a claim row is described to Raketech.
 *
 * Every row says which way it runs on Raketech's own figures, and, where Adwin
 * is asking for something, what. The four actions are the four things Raketech
 * has to answer: a figure it reported and later reduced, a figure its latest
 * report carries below its own June 2025 file, earnings it never reported at
 * all, and months only a mapping table can resolve.
 *
 * This lives here rather than in the page or the enclosure script because the
 * letter, the files sent with it and the shared link all have to say the same
 * thing about the same account.
 */
import type { ClaimRow } from '../types/raketechFinalReportV3.types';

export const MARKS = {
  writtenDown: 'Evidence needed - now carried below what Raketech reported at the time',
  restatedDown: 'Evidence needed - July 2026 report below Raketech\'s own June 2025 file',
  tailWrittenDown: 'Evidence needed - July 2026 report below Raketech\'s own monthly report, Dec 2024 onward',
  neverReported: 'Never reported to Adwin - no invoice was raised because no report was issued',
  remapped: 'Mapping needed - earnings recorded here, the same brand invoiced under another account id',
  gaps: 'Mapping needed - months invoiced that no Raketech source describes',
  backend: 'To reconcile at source - operator data records more than any Raketech figure',
  dealBasis: 'Deal terms needed - reported as acquisition only while the account earned revenue share',
} as const;

/** Below this a difference is not worth putting to anyone. */
const MATERIAL = 100;

export const POSITIONS = {
  short: 'Raketech\'s figure exceeds what Adwin invoiced',
  over: 'Adwin invoiced above Raketech\'s figure',
  level: 'No material difference',
} as const;

const r2 = (n: number) => Math.round(n * 100) / 100;

const brandKey = (s: string) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const candidate = (row: ClaimRow, source: string): number =>
  row.candidates.find((c) => c.source === source && c.available)?.amount || 0;

/** Which way the row runs, on Raketech's own figures. */
export function positionFor(row: ClaimRow): string {
  if (row.underpaid < -1) return POSITIONS.short;
  if (row.underpaid > 1) return POSITIONS.over;
  return POSITIONS.level;
}

/** Every month each brand carries invoicing in, under any account id. */
export function invoicedMonthsByBrand(rows: ClaimRow[]): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  for (const row of rows) {
    for (const brand of row.brands) {
      const key = brandKey(brand);
      if (!key) continue;
      const set = out.get(key) || new Set<string>();
      for (const m of row.monthly) if (m.invoiced > 0) set.add(m.month);
      out.set(key, set);
    }
  }
  return out;
}

/**
 * Earnings on an account Adwin raised no invoice against, split by whether the
 * brand was invoiced in that month at all.
 *
 * `never` is a month no invoice exists for anywhere: Raketech recorded earnings
 * it never reported, so nothing could be billed. `elsewhere` is a month the same
 * brand was invoiced under another account id, which is a mapping question and
 * is not additional revenue.
 */
export function unreported(row: ClaimRow, brandMonths: Map<string, Set<string>>) {
  if (row.earnedTotal <= 1 || row.invoicedTotal >= 1) return { never: 0, elsewhere: 0 };
  const invoiced = brandMonths.get(brandKey(row.brands[0] || '')) || new Set<string>();
  let never = 0;
  let elsewhere = 0;
  for (const m of row.monthly) {
    if (m.earned <= 0) continue;
    if (invoiced.has(m.month)) elsewhere += m.earned; else never += m.earned;
  }
  return { never: r2(never), elsewhere: r2(elsewhere) };
}

/** The reconciled period ends here; later months are only in the newer report. */
const RECON_END = '2024-11';

/**
 * The most Raketech's own files put on the account across the whole span: the
 * best of its figures for the reconciled period, plus what its July 2026 report
 * carries for the months after it. Anything above this is Adwin's own data.
 */
export function highestRaketechFigure(row: ClaimRow): number {
  const reconciled = Math.max(
    candidate(row, 'june_recon'),
    candidate(row, 'june_reported'),
    candidate(row, 'raketech_restated'),
  );
  const tail = row.monthly.reduce((t, m) => t + (m.month > RECON_END ? (m.restated || 0) : 0), 0);
  return r2(reconciled + tail);
}

/**
 * Months after the reconciled period in which Adwin invoiced, on Raketech's own
 * monthly report, more than Raketech's July 2026 report now carries for the
 * account. The monthly report is what Adwin billed on, so this is the same
 * write-down as `writtenDown`, on the later months: Raketech reported a figure,
 * was invoiced on it, and has since reduced it. Adwin invoices net of the 5%
 * and the report is gross from May 2025, so the ordinary case sits the other
 * way and is not counted; only months where the invoice exceeds the gross are.
 *
 * Carried on the row by v4 (`tailWrittenDown`); v3 rows do not have it. Set
 * only where it is worth putting to anyone: above the material threshold and
 * above 1% of what was invoiced in those months, so rounding between two of
 * Raketech's files does not read as a write-down.
 */
export function tailWrittenDown(row: ClaimRow): number {
  const v = (row as ClaimRow & { tailWrittenDown?: number }).tailWrittenDown || 0;
  if (v <= MATERIAL) return 0;
  if (v <= (row.invoicedPostRecon || 0) * 0.01) return 0;
  return r2(v);
}

/** By how much operator, manual or Affiliation Cloud data carries the row above it. */
export function backendLift(row: ClaimRow): number {
  const lift = row.earnedTotal - highestRaketechFigure(row);
  return lift > MATERIAL ? r2(lift) : 0;
}

/**
 * Revenue share earned in an account Raketech reported to Adwin as paying per
 * acquisition and nothing else.
 *
 * Clause 4.8 measures the fee by what the Gaming Operator pays or owes Raketech
 * on the Partner's New Users in a Partner Account, and 4.3.2 gives the Partner
 * that Net Revenue less 5%. The basis Raketech chose when reporting to Adwin
 * does not narrow that, so the gap is not written off: it is the amount whose
 * deal terms Raketech has to produce, and clause 4.10 makes remuneration agreed
 * with an operator and not disclosed to Adwin payable to Adwin less 5% in any
 * event.
 */
export function revenueShareOnAcquisitionDeal(row: ClaimRow): number {
  if (row.dealBasis !== 'acquisition_only') return 0;
  return backendLift(row);
}

/** What Adwin asks for on the row. Empty means the row is not in issue. */
export function actionsFor(row: ClaimRow, brandMonths: Map<string, Set<string>>): string[] {
  const out: string[] = [];
  // Their file crediting less than it reported is a question whatever values
  // the row, so this is not conditioned on the claim resting on it.
  if (row.reportedVsCredited > 1 || row.reportedLift > 1) out.push(MARKS.writtenDown);
  if (candidate(row, 'june_recon') - candidate(row, 'raketech_restated') > 1) out.push(MARKS.restatedDown);
  if (tailWrittenDown(row) > 0) out.push(MARKS.tailWrittenDown);
  const un = unreported(row, brandMonths);
  if (un.never > 0) out.push(MARKS.neverReported);
  if (un.elsewhere > 0) out.push(MARKS.remapped);
  if (row.coverage.gapMonths > 0) out.push(MARKS.gaps);
  // Said openly: this part of the difference is not evidenced by anything of
  // Raketech's, and it is the part only source data can settle.
  if (backendLift(row) > 0) out.push(MARKS.backend);
  if (revenueShareOnAcquisitionDeal(row) > 0) out.push(MARKS.dealBasis);
  return out;
}

export interface MarkTotals {
  writtenDownAccounts: number;
  writtenDownAmount: number;
  restatedAccounts: number;
  tailWrittenDownAccounts: number;
  tailWrittenDownAmount: number;
  neverAccounts: number;
  neverAmount: number;
  remapAccounts: number;
  remapAmount: number;
  backendAccounts: number;
  backendAmount: number;
  dealBasisAccounts: number;
  dealBasisAmount: number;
}

/** The counts the letter quotes, on the rule the rows are marked with. */
export function markTotals(rows: ClaimRow[], brandMonths: Map<string, Set<string>>): MarkTotals {
  const un = rows.map((r) => unreported(r, brandMonths));
  return {
    writtenDownAccounts: rows.filter((r) => r.reportedVsCredited > 1 || r.reportedLift > 1).length,
    writtenDownAmount: r2(rows.reduce(
      (t, r) => t + Math.max(r.reportedVsCredited > 1 ? r.reportedVsCredited : 0, r.reportedLift > 1 ? r.reportedLift : 0),
      0,
    )),
    restatedAccounts: rows.filter((r) => candidate(r, 'june_recon') - candidate(r, 'raketech_restated') > 1).length,
    tailWrittenDownAccounts: rows.filter((r) => tailWrittenDown(r) > 0).length,
    tailWrittenDownAmount: r2(rows.reduce((t, r) => t + tailWrittenDown(r), 0)),
    neverAccounts: un.filter((u) => u.never > 0).length,
    neverAmount: r2(un.reduce((t, u) => t + u.never, 0)),
    remapAccounts: un.filter((u) => u.elsewhere > 0).length,
    remapAmount: r2(un.reduce((t, u) => t + u.elsewhere, 0)),
    backendAccounts: rows.filter((r) => backendLift(r) > 0).length,
    backendAmount: r2(rows.reduce((t, r) => t + backendLift(r), 0)),
    dealBasisAccounts: rows.filter((r) => revenueShareOnAcquisitionDeal(r) > 0).length,
    dealBasisAmount: r2(rows.reduce((t, r) => t + revenueShareOnAcquisitionDeal(r), 0)),
  };
}
